<script lang="ts" setup>
import { computed, reactive, ref, Ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@/utils/validators';

import { goBack } from '@/composables/useNavigation';
import { useUiStore } from '@/stores';
import { QueueService, SelectOptionsService, VaultService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { getAudioFormat } from '@/utils/audio';

import {
    FormAction,
    MultiSelectOption,
    Permission,
    QueueFormState,
    QueueModel,
    QueueStrategy,
    SelectOptionsServiceGetUsersResult,
} from '@/types';

import { AxiosResponse } from 'axios';

const props = defineProps({
    action: { type: String, default: FormAction.CREATE },
});

const route = useRoute();
const uiStore = useUiStore();
const { t } = useI18n();

const formState = reactive<QueueFormState>({
    name: '',
    strategy: '',
    users: [],
    fileAudio: '',
    isActive: true,
});

const file = ref<File | null>(null);
const oldFileName = ref<string | null>(null);
const audioRemoved = ref(false);
const audioPreview = ref<string | null>(null);
const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);
const strategyOptions: Ref<MultiSelectOption[]> = ref([
    { label: t(`queue.strategy.${QueueStrategy.RANDOM}`), value: QueueStrategy.RANDOM },
    { label: t(`queue.strategy.${QueueStrategy.RRORDERED}`), value: QueueStrategy.RRORDERED },
    { label: t(`queue.strategy.${QueueStrategy.RRMEMORY}`), value: QueueStrategy.RRMEMORY },
    { label: t(`queue.strategy.${QueueStrategy.RINGALL}`), value: QueueStrategy.RINGALL },
    { label: t(`queue.strategy.${QueueStrategy.FEWESTCALLS}`), value: QueueStrategy.FEWESTCALLS },
    { label: t(`queue.strategy.${QueueStrategy.LEASTRECENT}`), value: QueueStrategy.LEASTRECENT },
]);

const rules = computed<ValidationArgs<QueueFormState>>(() => ({
    name: { required, maxLength: maxLength(50) },
    strategy: { required },
    users: {},
    fileAudio: {},
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<QueueFormState>>> = useVuelidate(rules, formState);

watch([file], () => {
    if (file.value) {
        formState.fileAudio = file.value.name;
        audioPreview.value = URL.createObjectURL(file.value);
    } else {
        formState.fileAudio = '';
        audioPreview.value = null;
        audioRemoved.value = oldFileName.value !== null;
    }
});

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchUsers = async () => {
    try {
        const { data } = await SelectOptionsService.getUsers({
            params: {
                has_extension: true,
                is_active: true,
            },
        });
        usersOptions.value = data;
    } catch (e) {
        toastServiceError(e);
    }
};

const fetchQueue = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await QueueService.get<QueueModel>(id);
        formState.name = data.name;
        formState.strategy = data.strategy;
        formState.users = (data.users || []).map((u) => u.id);
        formState.fileAudio = data.fileAudio;
        oldFileName.value = data.fileAudio;
        formState.isActive = data.isActive;
        await loadAudio();
    } catch (e) {
        toastServiceError(e);
    } finally {
        setLoading(false);
    }
};

const loadAudio = async () => {
    if (formState.fileAudio) {
        try {
            const { data } = await VaultService.getAudioAsset(formState.fileAudio);
            const blob = new Blob([data], { type: getAudioFormat(formState.fileAudio) });
            file.value = new File([blob], formState.fileAudio, {
                type: blob.type,
                lastModified: Date.now(),
            });
        } catch (e) {
            toastServiceError(e);
        }
    }
};

const onSubmit = async () => {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    setLoading(true);

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({ name: 'ViewQueueById', params: { id } });
    };

    try {
        const formData = new FormData();
        formData.append('name', formState.name);
        formData.append('strategy', formState.strategy);
        formData.append('isActive', formState.isActive.toString());
        formState.users.forEach((user, index) => {
            formData.append(`users[${index}]`, user.toString());
        });
        const hasFileChanged = (file.value?.name ?? null) !== oldFileName.value;

        if (file.value && hasFileChanged) formData.append('audio', file.value);

        if (audioRemoved.value) formData.append('audioRemoved', 'true');

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data }: AxiosResponse = await QueueService.update(id, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, id);
        } else {
            const { data }: AxiosResponse = await QueueService.create(formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (e) {
        toastServiceError(e);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchUsers();
    if (props.action === FormAction.EDIT) {
        await fetchQueue(Number(route.params.id));
    }
});
</script>

<template>
    <form class="space-y-4 p-4 sm:p-6 lg:p-8" autocomplete="off" @submit.prevent="onSubmit">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('queue.' + props.action + '.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex items-center space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListQueue' }" />
                    <UiButton
                        v-if="
                            (props.action == FormAction.CREATE && $can(Permission.CREATE_QUEUE)) ||
                            (props.action == FormAction.EDIT && $can(Permission.EDIT_QUEUE))
                        "
                        type="submit"
                        variant="primary"
                        text-variant="white"
                    >
                        {{ $t('core.actions.Save') }}
                    </UiButton>
                </div>
            </template>
        </UiPageHeader>
        <UiPanel class="grid grid-cols-1 gap-4 md:grid-cols-3">
            <UiTextInput
                v-model="v$.name.$model"
                name="name"
                type="text"
                :label="$t('queue.labels.name')"
                :errors="v$.name.$errors"
                @blur="v$.name.$touch"
            />

            <div>
                <label for="level" class="mb-2 block text-sm font-medium text-gray-800">
                    {{ $t('queue.labels.strategy') }}
                </label>
                <UiMultiselect
                    id="strategy"
                    name="strategy"
                    v-model="formState.strategy"
                    primitive
                    :options="strategyOptions"
                />
            </div>

            <div>
                <label for="users" class="mb-2 block text-sm font-medium text-gray-800">
                    {{ $t('queue.labels.users') }}
                </label>
                <UiMultiselect
                    id="users"
                    name="users"
                    label="name"
                    value-prop="id"
                    v-model="formState.users"
                    multiple
                    primitive
                    :options="usersOptions"
                />
            </div>

            <div class="flex flex-col justify-end pb-1.5">
                <UiCheckboxInput
                    v-model="v$.isActive.$model"
                    name="isActive"
                    :label="$t('queue.labels.isActive')"
                    class="h-6"
                />
            </div>
        </UiPanel>

        <!-- Audio upload -->
        <UiPanel class="flex flex-col space-y-2">
            <label class="block text-sm font-medium text-gray-800">
                {{ $t('queue.labels.audioUpload') }}
            </label>
            <UiAudioUpload
                v-model:file="file"
                :text-rule="$t('queue.labels.uploadRules')"
                :errors="v$.fileAudio.$errors"
            />
            <div
                class="text-danger bg-danger-100 p-2 text-sm shadow"
                v-if="v$.fileAudio.$errors && v$.fileAudio.$errors.length"
            >
                <p v-for="error of v$.fileAudio.$errors" :key="error.$uid">
                    {{ error.$message }}
                </p>
            </div>
        </UiPanel>

        <!-- Audio preview -->
        <UiPanel v-if="audioPreview" class="space-y-2">
            <label class="block text-sm font-medium text-gray-800">{{
                $t('queue.labels.audioPreview')
            }}</label>
            <audio controls class="w-full">
                <source :src="audioPreview" />
                Your browser does not support the audio element.
            </audio>
        </UiPanel>

        <UiCheckDirtyBeforeRouteLeave :dirty="v$.$anyDirty" />
    </form>
</template>
